import GoingUtils from "@/utils/goingUtils";
import {deepClone} from 'xmgoing-utils';
/**
 * 根据含税单价和数量计算含税总金额
 * @param {number} taxIncludedUnitPrice - 含税单价
 * @param {number} quantity - 数量
 * @returns {number} 含税总金额
 */
export function calculateTaxIncludedTotal(taxIncludedUnitPrice, quantity) {
  return taxIncludedUnitPrice * quantity;
}

/**
 * * 计算未含税单价（不含税单价）
 * * @param {number} taxIncludedPrice - 含税单价（单位：元）
 * * @param {number} taxRate - 税率（以小数形式表示，例如 13% 的税率为 0.13）
 * * @returns {number} 未含税单价（保留两位小数）
 */
export function calculateUnitPriceWithoutTax(taxIncludedPrice, taxRate,fixNumber=2) {
  taxIncludedPrice=parseFloat(taxIncludedPrice);
  if(parseFloat(taxRate)>1){
    taxRate=parseFloat(taxRate)/100;
  }
  // 参数校验：确保输入为有效数字且税率合理
  if (isNaN(taxIncludedPrice) || isNaN(taxRate) || taxRate <= 0 || taxRate >= 1) {
    return ;
  }

  // 核心计算公式：不含税单价 = 含税单价 / (1 + 税率)
  const unitPriceWithoutTax = taxIncludedPrice / (1 + taxRate);

  if(fixNumber===2){
    // 保留两位小数（财务精度要求）
    return Math.round(unitPriceWithoutTax * 100) / 100;
  }else{
    // 保留两位小数（财务精度要求）
    return Math.round(unitPriceWithoutTax * 1000) / 1000;
  }
}
/**
 * 根据含税单价和数量计算不含税总金额
 * 需要知道税率来计算
 * @param {number} taxIncludedUnitPrice - 含税单价
 * @param {number} quantity - 数量
 * @param {number} taxRate - 税率（例如 0.1 表示 10%）
 * @returns {number} 不含税总金额
 */
export function calculateOriginalTotal(taxIncludedUnitPrice, quantity, taxRate) {
  taxRate=taxRate/100
  return taxIncludedUnitPrice * quantity / (1 + taxRate);
}


/**
 * 根据税额和含税单价计算不含税单价
 * @param {number} taxAmount - 税额（必须为数字且非负）
 * @param {number} taxInclusivePrice - 含税单价（必须为数字且大于税额）
 * @param {number} [precision=2] - 保留小数位数（默认2位）
 * @returns {number} 不含税单价
 */
export function getTaxExclusivePrice(taxAmount, taxInclusivePrice, precision = 2) {
  // 参数校验
  if (typeof taxAmount !== 'number' || typeof taxInclusivePrice !== 'number') {
    // throw new Error('税额和含税单价必须是数字');
    return 0;
  }
  if (taxAmount < 0 || taxInclusivePrice < 0) {
    // throw new Error('金额不能为负数');
    return 0;
  }
  if (taxAmount > taxInclusivePrice) {
    // throw new Error('税额不能超过含税单价');
    return 0;
  }
  // 核心计算逻辑：不含税单价 = 含税单价 - 税额
  const taxExclusivePrice = taxInclusivePrice - taxAmount;
  // 精度处理（默认保留两位小数）
  return Number(taxExclusivePrice.toFixed(precision));
}
/**
 * 根据含税单价和数量计算税额
 * @param {number} taxIncludedUnitPrice - 含税单价
 * @param {number} quantity - 数量
 * @param {number} taxRate - 税率（例如 0.1 表示 10%）
 * @returns {number} 税额
 */
export function calculateTaxAmount(taxIncludedUnitPrice, quantity, taxRate) {
  taxRate=taxRate/100
  const taxIncludedTotal = calculateTaxIncludedTotal(taxIncludedUnitPrice, quantity);
  const originalTotal = calculateOriginalTotal(taxIncludedUnitPrice, quantity, taxRate);
  return taxIncludedTotal - originalTotal;
}

/** 按指定小数位四舍五入；合计相关金额默认 2 位，单价/折扣率传 6 */
const round = (num, digits = 2) => {
  const factor = Math.pow(10, digits);
  return Math.round(Number(num) * factor) / factor;
};

/**
 * 折扣额计算
 * @param totalDiscount 整单折扣额
 * @param materialsList 物料列表
 * @param useActQuantity 是否启用实发数量，类型为布尔值，用于确定是否在计算折扣等字段时使用实发数量，具体业务含义根据实际场景确定
 */
export function calculateAndAllocateDiscount(totalDiscount, materialsListDb, useActQuantity = false) {
  const materialsList=deepClone(materialsListDb);
  console.log('开始计算整单折扣---------------------', totalDiscount, materialsList)
  // 参数校验
  if (!Array.isArray(materialsList) || materialsList.length === 0) {
    return materialsList;
  }
  useActQuantity = useActQuantity === true; // 兼容有些旧的地方传了字符串

  // 计算总原始金额（使用不含税单价）
  let totalOriginalAmount = 0;
  materialsList.forEach(material => {
    let qty, unitPrice, taxRate;
    // 根据useActQuantity参数决定使用哪个数量字段
    qty = useActQuantity ? (parseFloat(material.actualQuantity) || 0) : (parseFloat(material.quantity) || 0);
    unitPrice = parseFloat(material.unitPrice) || 0;
    taxRate = parseFloat(material.taxRate) || 0;
    // 金额 = 单价 × 数量
    const originalAmount = unitPrice * qty;
    // 税额 = 金额 × 税率
    const originalTaxAmount = originalAmount * (taxRate / 100);
    // 价税合计 = 金额 + 税额
    totalOriginalAmount += originalAmount + originalTaxAmount;
  });

  // 如果没有原始金额，全部置零
  if (totalOriginalAmount <= 0) {
    materialsList.forEach(material => {
      material.discountAmount = 0;
      material.unitDiscountRate = 0;
      material.totalPriceIncludingTax = 0;
      material.amount = 0;
      material.taxAmount = 0;
    });
    return materialsList;
  }

  // 按比例分摊折扣额
  let allocatedDiscount = 0;
  materialsList.forEach((material, index) => {
    // qty: 数量，unitPrice: 无税单价，taxRate: 税率
    let qty: number, unitPrice: number, taxRate: number;
    // 根据useActQuantity参数决定使用哪个数量字段
    qty = useActQuantity ? (parseFloat(material.actualQuantity) || 0) : (parseFloat(material.quantity) || 0);
    unitPrice = parseFloat(material.unitPrice) || 0;
    taxRate = parseFloat(material.taxRate) || 0;
    const taxRateDecimal = taxRate / 100;

    // 使用不含税单价计算原始金额
    const originalAmount = unitPrice * qty;
    // 税额 = 金额 × 税率
    const originalTaxAmount = originalAmount * taxRateDecimal;
    // 价税合计 = 金额 + 税额
    const originalTotalWithTax = originalAmount + originalTaxAmount;

    // 计算折扣额分摊比例（基于价税合计）
    const proportion = totalOriginalAmount > 0 ? originalTotalWithTax / totalOriginalAmount : 0;
    // 计算该行的折扣额（合计相关金额：到分）
    let discountAmount = 0;
    if (materialsList.length === 1) {
      discountAmount = round(totalDiscount);
    } else if (index === materialsList.length - 1) {
      // 将前几项的折扣额合计减去（到分），得到最后一项的折扣额
      discountAmount = round(totalDiscount - allocatedDiscount);
    } else {
      discountAmount = round(totalDiscount * proportion);
      allocatedDiscount += discountAmount;
    }

    // 按比例分摊折扣到金额；折扣后价税合计钉住「原价税合计 − 折扣额」
    const discountProportion = originalTotalWithTax > 0 ? discountAmount / originalTotalWithTax : 0;

    // 金额 = 单价 × 数量 - 金额分摊的折扣
    const discountedAmount = round(originalAmount - originalAmount * discountProportion);

    // 折扣后价税合计 = 原价税合计 − 折扣额（到分），不得被金额+税额二次到分冲掉
    const discountedTotalAmount = round(originalTotalWithTax - discountAmount);

    // 税额 = 价税合计 − 金额（到分尾差落在税额）
    const discountedTaxAmount = round(discountedTotalAmount - discountedAmount);

    // 赋值
    material.discountAmount = discountAmount;
    material.totalPriceIncludingTax = discountedTotalAmount;
    material.amount = discountedAmount;
    material.taxAmount = discountedTaxAmount;
    // 折扣率（百分比，保留6位小数）
    const discountRate = originalTotalWithTax > 0 ? round(discountAmount / originalTotalWithTax, 6) : 0;
    material.unitDiscountRate = round(discountRate * 100, 6);
    console.log('更新后的行数据------------', material);
  });
  return materialsList;
}

/**
 * 更新物料行数据并联动计算相关字段
 * @param {Object} record - 当前行数据对象
 * @param {string} field - 变化的字段名
 * @param {number} cellValue - 变化后的值
 * @param {Object} formObj - 整个表单对象
 */
export function updateMaterialRow(record, field, cellValue, formObj, enterType = '', params = {}) {
  console.log('开始更新行内字段----', record, field, cellValue, formObj);

  let unitPriceType = formObj?.unitPriceType || 'unitPriceByInclude'; // unitPriceType - 单价类型，"未税"表示不含税单价，其他或不传表示含税单价
  if (enterType !== 'saleOrder' && enterType !== 'purchaseOrder' && unitPriceType === 'totalPriceByInclude') {
    // 只有销售订单和采购订单，才支持录入价税合计模式
    unitPriceType = 'unitPriceByInclude';
  }
  // 判断是否使用实际出库数量进行计算
  const useActualQuantity = formObj?.actOutStockQuantityFlag === true || formObj?.actOutStockQuantityFlag === 'true';

  // round(x)：合计相关金额默认到分；round(x, 6)：单价/折扣率

  // 更新变化的字段
  let newValue = 0;
  if (typeof cellValue === 'number') {
    newValue = cellValue;
  } else if (typeof cellValue === 'string' && cellValue.trim() !== '' && !isNaN(Number(cellValue))) {
    newValue = parseFloat(cellValue);
  }

  if (newValue < 0 && (params['scene'] !== 'canBeNegative')) {
    // 输入的值不能为负数
    newValue = 0;
    GoingUtils.showMessage('输入的值不能为负数')
  }
  record[field] = newValue;
  if (useActualQuantity && field === 'quantity') {
    // 修改数量时 同步修改实发数量
    record.actualQuantity = newValue;
  }
  
  // 字段标准化
  let priceWithTax, unitPrice, taxRate, taxRateDecimal;
  priceWithTax = Number(record.priceIncludingTax) || 0;
  unitPrice = Number(record.unitPrice) || 0;
  taxRate = Number(record.taxRate) || 0;
  taxRateDecimal = taxRate / 100;
  // 获取实际使用的数量值
  const actualQty = useActualQuantity ? (Number(record.actualQuantity) || 0) : (Number(record.quantity) || 0);

  // 联动逻辑
  let discountAmount = Number(record.discountAmount) || 0;
  let unitDiscountRate = Number(record.unitDiscountRate) || 0;

  // 处理单价变化
  if (field === 'priceIncludingTax' || field === 'quantity' || field === 'unitPrice' || field === 'taxRate' || field === 'actualQuantity' || field === 'totalPriceIncludingTax' || field === 'discountAmount') {
    // 重新取值
    priceWithTax = Number(record.priceIncludingTax) || 0;
    unitPrice = Number(record.unitPrice) || 0;
    taxRate = Number(record.taxRate) || 0;
    taxRateDecimal = taxRate / 100;
    
    // 当单价类型为"totalPriceByInclude"时，以总价模式进行计算
    if (unitPriceType === "totalPriceByInclude") {
      // 在总价模式下，totalPriceIncludingTax 是折扣后的价税合计
      const discountedTotalWithTax = Number(record.totalPriceIncludingTax) || 0;

      // 计算原始价税合计 = 折扣后价税合计 + 折扣额
      const originalTotalWithTax = round(discountedTotalWithTax + discountAmount);

      // 当数量大于0时才进行计算
      if (actualQty > 0) {
        // 根据原始价税合计反推单价
        // 当税率为0时，不含税单价等于价税合计除以数量
        if (taxRateDecimal === 0) {
          record.unitPrice = round(originalTotalWithTax / actualQty, 6);
        } else {
          // 不含税单价 = 原始价税合计 / (数量 * (1 + 税率))
          record.unitPrice = round(originalTotalWithTax / actualQty / (1 + taxRateDecimal), 6);
        }
        unitPrice = record.unitPrice;

        // 含税单价 = 不含税单价 * (1 + 税率)
        if (taxRateDecimal === 0) {
          record.priceIncludingTax = unitPrice;
        } else {
          record.priceIncludingTax = round(unitPrice * (1 + taxRateDecimal), 6);
        }
        priceWithTax = record.priceIncludingTax;
      }

      // 根据单价计算原始金额
      // 原始金额 = 单价 × 数量
      const originalAmount = round(actualQty * unitPrice);

      // 计算折扣比例
      const discountProportion = originalTotalWithTax > 0 ? discountAmount / originalTotalWithTax : 0;

      // 按比例分摊折扣到金额
      // 折扣后金额 = 原始金额 × (1 - 折扣比例)
      const amount = round(originalAmount * (1 - discountProportion));
      record.amount = amount;

      // 录入价税合计模式：折扣后价税合计为权威，不得被金额+税额二次到分冲掉
      const totalPriceIncludingTax = round(discountedTotalWithTax);
      record.totalPriceIncludingTax = totalPriceIncludingTax;

      // 税额 = 价税合计 − 金额（到分尾差落在税额）
      record.taxAmount = round(totalPriceIncludingTax - amount);

      // 原始价税合计
      record.originalTotalWithTax = originalTotalWithTax;

      // 重新计算折扣率
      record.unitDiscountRate = originalTotalWithTax > 0 ? round((discountAmount / originalTotalWithTax) * 100, 6) : 0;
    }
    // 当单价类型为"unitPriceByExclude"未税时，以单价和税率计算含税单价
    else if (unitPriceType === "unitPriceByExclude") {
      // 当税率为0时，含税单价等于不含税单价
      if (taxRateDecimal === 0) {
        record.priceIncludingTax = unitPrice;
      } else {
        // 含税单价 = 不含税单价 * (1 + 税率)
        record.priceIncludingTax = round(unitPrice * (1 + taxRateDecimal), 6);
      }
      priceWithTax = record.priceIncludingTax;

      // 折扣率联动
      // 金额 = 单价 × 数量
      const amount = round(actualQty * unitPrice);
      // 税额 = 金额 × 税率
      const taxAmount = round(amount * taxRateDecimal);
      // 价税合计 = 金额 + 税额
      const originalTotalWithTax = round(amount + taxAmount);
      record.originalTotalWithTax = originalTotalWithTax;
      record.unitDiscountRate = originalTotalWithTax > 0 ? round((discountAmount / originalTotalWithTax) * 100, 6) : 0;
    } 
    // 否则以含税单价和税率计算不含税单价（与后端 StandardAmountCalculator 同锚）
    else {
      // 当税率为0时，不含税单价等于含税单价
      if (taxRateDecimal === 0) {
        record.unitPrice = priceWithTax;
      } else {
        // 不含税单价 = 含税单价 / (1 + 税率)
        record.unitPrice = round(priceWithTax / (1 + taxRateDecimal), 6);
      }
      unitPrice = record.unitPrice;

      // 原价税合计钉住含税单价 × 数量（到分），不得用金额+税额二次到分覆盖
      const originalTotalWithTax = round(priceWithTax * actualQty);
      record.originalTotalWithTax = originalTotalWithTax;
      record.unitDiscountRate = originalTotalWithTax > 0 ? round((discountAmount / originalTotalWithTax) * 100, 6) : 0;
    }
  }

  // 折扣率和折扣额联动（仅在非总价模式下）
  if (unitPriceType !== "totalPriceByInclude") {
    const priceWithTaxNow = Number(record.priceIncludingTax) || 0;
    const unitPriceNow = Number(record.unitPrice) || 0;
    const isIncludeMode = unitPriceType !== "unitPriceByExclude";
    // 金额 = 未税单价 × 数量（到分）
    const originalAmount = round(actualQty * unitPriceNow);
    // 含税模式：原价税合计 = 含税单价 × 数量；未税模式：原价税合计 = 金额 + 税额
    const originalTotalWithTax = isIncludeMode
      ? round(priceWithTaxNow * actualQty)
      : round(originalAmount + round(originalAmount * taxRateDecimal));

    if (field === 'unitDiscountRate') {
      discountAmount = round(originalTotalWithTax * unitDiscountRate / 100);
      record.discountAmount = discountAmount;
    } else if (field === 'discountAmount') {
      unitDiscountRate = originalTotalWithTax > 0 ? round((discountAmount / originalTotalWithTax) * 100, 6) : 0;
      record.unitDiscountRate = unitDiscountRate;
    }

    const lineDiscount = Number(record.discountAmount) || 0;
    const discountProportion = originalTotalWithTax > 0 ? lineDiscount / originalTotalWithTax : 0;
    // 按比例折扣金额
    let amount = round(originalAmount * (1 - discountProportion));
    let taxAmount;
    let totalPriceIncludingTax;
    if (isIncludeMode) {
      // 含税权威：折扣后价税合计钉住原价税合计 − 折扣额；税额 = 价税合计 − 金额
      totalPriceIncludingTax = round(originalTotalWithTax - lineDiscount);
      taxAmount = round(totalPriceIncludingTax - amount);
    } else {
      // 未税权威：税额正算，价税合计 = 金额 + 税额
      taxAmount = round(amount * taxRateDecimal);
      totalPriceIncludingTax = round(amount + taxAmount);
    }
    record.originalTotalWithTax = originalTotalWithTax;
    record.totalPriceIncludingTax = totalPriceIncludingTax;
    record.amount = amount;
    record.taxAmount = taxAmount;
  }

  // 反向计算（如直接改了untaxedTotal或taxAmount，但不适用于总价模式）
  if (unitPriceType !== "totalPriceByInclude" && ['taxAmount', 'totalPriceIncludingTax'].includes(field)) {
    // 价税合计 = 金额 + 税额
    record.totalPriceIncludingTax = round((Number(record.amount) || 0) + (Number(record.taxAmount) || 0));
    // 计算原始价税合计
    const amount = round(actualQty * (Number(record.unitPrice) || 0));
    const taxAmount = round(amount * taxRateDecimal);
    const originalTotalWithTax = round(amount + taxAmount);
    record.discountAmount = round(Math.max(0, originalTotalWithTax - record.totalPriceIncludingTax));
    record.unitDiscountRate = originalTotalWithTax > 0 ? round((record.discountAmount / originalTotalWithTax) * 100, 6) : 0;
  }

  // 确保数值合法性
  // [
  //   'priceIncludingTax', 'unitPrice', 'originalTotalWithTax',
  //   'discountAmount', 'totalPriceIncludingTax',
  //   'taxAmount', 'unitDiscountRate'
  // ].forEach(key => {
  //   if (isNaN(record[key])) record[key] = 0;
  //   if (record[key] < 0) record[key] = 0;
  // });
  if (record.unitDiscountRate > 100) record.unitDiscountRate = 100;

  if (cellValue === null || (typeof cellValue === 'string' && cellValue.trim() === '')) {
    // 输入值是空格
    record[field] = cellValue;
  }
  console.log('更新后的行数据------------', record);
}

export function splitMaterialWithQuantity(material, record, useActQuantity = false) {
  // 根据useActQuantity参数决定使用哪个数量字段
  useActQuantity = useActQuantity === true; // 兼容有些旧的地方传了字符串
  let quantity = useActQuantity ? record.actualQuantity : record.quantity;
  if (typeof quantity === 'string' && quantity.trim() !== '' && !isNaN(Number(quantity))) {
    quantity = parseFloat(quantity);
  }
  if (quantity <= 0) {
    return;
  }
  
  // 同样根据useActQuantity参数获取总量
  let totalQuantity = useActQuantity ? material.actualQuantity : material.quantity;
  if (typeof totalQuantity === 'string' && totalQuantity.trim() !== '' && !isNaN(Number(totalQuantity))) {
    totalQuantity = parseFloat(totalQuantity);
  }
  // 当useActQuantity为true时，即使totalQuantity<=0也可以继续执行，因为不需要严格保持数量一致
  if (!useActQuantity && totalQuantity <= 0) {
    return;
  }

  // 使用小数位对齐后的整数计算比例，降低精度误差
  const toSafeRatio = (num, den) => {
    const sNum = String(num);
    const sDen = String(den);
    const decNum = (sNum.split('.')[1] || '').length;
    const decDen = (sDen.split('.')[1] || '').length;
    const scale = Math.pow(10, Math.max(decNum, decDen));
    const intNum = Math.round(num * scale);
    const intDen = Math.round(den * scale);
    if (intDen <= 0) return 0;
    let r = intNum / intDen;
    if (!isFinite(r) || isNaN(r)) r = 0;
    if (r < 0) r = 0;
    if (r > 1) r = 1;
    // 高精度保留（10位），再用于后续计算
    return Math.round(r * 1e10) / 1e10;
  };
  const proportion = toSafeRatio(quantity, totalQuantity);

  // 保持以下字段不变（从原始物料同步，确保一致性）
  record.priceIncludingTax = parseFloat(material.priceIncludingTax) || 0; // 含税单价
  record.unitPrice = parseFloat(material.unitPrice) || 0; // 不含税单价
  record.taxRate = parseFloat(material.taxRate) || 0; // 税率
  record.unitDiscountRate = parseFloat(material.unitDiscountRate) || 0; // 折扣率（百分比）

  const taxRateDecimal = record.taxRate / 100;

  // 使用不含税单价计算原始金额 / 原价税合计（与整单折扣同一套未先到分的原值，收尾再到分）
  const rawAmount = record.unitPrice * quantity;
  let originalAmount = round(rawAmount);
  let originalTotalWithTax = round(rawAmount * (1 + taxRateDecimal));

  // 按比例分摊折扣额（使用高精度比例，合计相关金额到分）
  let discountAmount = parseFloat(material.discountAmount) || 0;
  discountAmount = round(discountAmount * proportion);
  if (discountAmount < 0) discountAmount = 0;

  // 计算折扣比例
  const discountProportion = originalTotalWithTax > 0 ? discountAmount / originalTotalWithTax : 0;

  // 折扣后金额（不含税）= 原始金额 × (1 - 折扣比例)
  let amount = round(rawAmount * (1 - discountProportion));
  if (amount < 0) amount = 0;

  // 折扣后价税合计钉住「原价税合计 − 折扣额」，不得被金额+税额二次到分冲掉
  let totalPriceIncludingTax = round(rawAmount * (1 + taxRateDecimal) - discountAmount);
  if (totalPriceIncludingTax < 0) totalPriceIncludingTax = 0;

  // 税额 = 价税合计 − 金额（到分尾差落在税额）
  let taxAmount = round(totalPriceIncludingTax - amount);
  if (taxAmount < 0) taxAmount = 0;

  // 赋值到record
  if (useActQuantity) {
    record.actualQuantity = quantity;
  } else {
    record.quantity = quantity;
  }
  record.originalTotalWithTax = originalTotalWithTax; // 原价税合计
  record.totalPriceIncludingTax = totalPriceIncludingTax; // 价税合计
  record.amount = amount; // 金额
  record.taxAmount = taxAmount; // 税额
  record.discountAmount = discountAmount; // 折扣额
}